import { useEffect, useState } from 'react';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { CheckRow } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useNotificationPrefs } from '@/lib/queries';
import { useUpdateNotificationPrefs } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import type { NotificationPrefs } from '@/types/api';

const OPTIONS: Array<{ key: keyof NotificationPrefs; label: string; description: string }> = [
  {
    key: 'paymentArrived',
    label: 'Мне направлен платёж',
    description: 'Единственное уведомление, которое стоит держать включённым — отсюда идут тридцать минут.',
  },
  {
    key: 'deadlineWarning',
    label: 'Осталось пять минут на подтверждение',
    description: 'Последнее напоминание перед истечением срока.',
  },
  {
    key: 'disputeResolved',
    label: 'По спору принято решение',
    description: 'Когда администратор выносит решение по вашей заявке.',
  },
  {
    key: 'kycUpdates',
    label: 'Изменился статус верификации',
    description: 'Одобрение, отказ или проверка карты.',
  },
  {
    key: 'emailDigest',
    label: 'Ежедневная сводка на почту',
    description: 'Объём, заработок и открытые споры за прошедший день.',
  },
];

export function NotificationsSection() {
  const toast = useToast();
  const prefs = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();
  const [draft, setDraft] = useState<NotificationPrefs | null>(null);

  useEffect(() => {
    if (prefs.data) setDraft(prefs.data);
  }, [prefs.data]);

  const isDirty =
    draft !== null &&
    prefs.data !== undefined &&
    OPTIONS.some(({ key }) => draft[key] !== prefs.data[key]);

  const save = async (): Promise<void> => {
    if (!draft) return;
    try {
      await update.mutateAsync(draft);
      toast.success('Настройки уведомлений сохранены');
    } catch (error) {
      toast.error('Не удалось сохранить настройки', toMessage(error));
    }
  };

  return (
    <Panel padding="none">
      <PanelHeader
        title="Уведомления"
        description="Уведомления внутри интерфейса всегда показываются на экране «Трафик»; здесь настраивается всё остальное."
      />

      <div className="px-4 py-3">
        {prefs.isPending ? (
          <div className="space-y-3 py-2" role="status" aria-busy="true">
            <span className="sr-only">Загружаю настройки уведомлений</span>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="flex gap-3">
                <Skeleton className="h-4 w-4" />
                <div className="flex-1">
                  <Skeleton className="h-3.5 w-52" />
                  <Skeleton className="mt-1.5 h-3 w-72" />
                </div>
              </div>
            ))}
          </div>
        ) : prefs.isError ? (
          <InlineError
            className="my-2"
            message={toMessage(prefs.error, 'Не удалось загрузить настройки уведомлений.')}
            onRetry={() => void prefs.refetch()}
          />
        ) : draft ? (
          <>
            <div className="divide-y divide-line">
              {OPTIONS.map(({ key, label, description }) => (
                <CheckRow
                  key={key}
                  label={label}
                  description={description}
                  checked={draft[key]}
                  disabled={update.isPending}
                  onChange={(next) => setDraft({ ...draft, [key]: next })}
                />
              ))}
            </div>

            <div className="border-t border-line py-3">
              <Button
                variant="primary"
                disabled={!isDirty}
                isLoading={update.isPending}
                onClick={() => void save()}
              >
                {update.isPending ? 'Сохраняю…' : 'Сохранить настройки'}
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Panel>
  );
}
